import { useCallback, useEffect, useState } from 'react';
import { safeLocalStorageGetItem, safeLocalStorageSetItem } from '../utils/safeStorage';

const HIDDEN_STORAGE_KEY = 'forge-sidebar-hidden';
const COLLAPSED_WIDTH = 38;
const MIN_WIDTH = 38;
const MAX_WIDTH = 400;
const EXPANDED_WIDTH = 138;

export function useSidebar() {
  const [width, setWidth] = useState(EXPANDED_WIDTH);
  const [isHidden, setIsHidden] = useState(safeLocalStorageGetItem(HIDDEN_STORAGE_KEY) === 'true');
  const [isResizing, setIsResizing] = useState(false);

  const isCollapsed = width <= COLLAPSED_WIDTH;

  const toggleCollapse = useCallback(() => {
    if (isCollapsed) {
      setWidth(EXPANDED_WIDTH);
    } else {
      setWidth(COLLAPSED_WIDTH);
    }
  }, [isCollapsed]);

  const hideSidebar = useCallback(() => {
    setIsHidden(true);
    safeLocalStorageSetItem(HIDDEN_STORAGE_KEY, 'true');
  }, []);

  const showSidebar = useCallback(() => {
    setWidth(EXPANDED_WIDTH);
    setIsHidden(false);
    safeLocalStorageSetItem(HIDDEN_STORAGE_KEY, 'false');
  }, []);

  // Drag-to-resize logic
  const onDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    document.body.classList.add('is-resizing');

    const onMove = (ev: MouseEvent) => {
      let newWidth = ev.clientX;
      if (newWidth < MIN_WIDTH) newWidth = MIN_WIDTH;
      if (newWidth > MAX_WIDTH) newWidth = MAX_WIDTH;
      setWidth(newWidth);
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.classList.remove('is-resizing');
      setIsResizing(false);

      setWidth((current) => {
        const final = current <= COLLAPSED_WIDTH ? COLLAPSED_WIDTH : current;
        return final;
      });
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  // Sync body class
  useEffect(() => {
    if (isResizing) {
      document.body.classList.add('is-resizing');
    } else {
      document.body.classList.remove('is-resizing');
    }
    return () => document.body.classList.remove('is-resizing');
  }, [isResizing]);

  return {
    width,
    isCollapsed,
    isHidden,
    isResizing,
    toggleCollapse,
    hideSidebar,
    showSidebar,
    onDragStart,
  } as const;
}
